import { AccountFeatureFlag, accountHasFeatureFlag } from './feature-flags';
import type { Account, AugmentedAccount, RawAccount } from './types';

/**
 * Canonical slug for the Builder plan. Mirrors `BuilderPlanSlug` in
 * customerio/services. Use `isBuilderPlan` rather than comparing the string
 * directly so there's a single source of truth.
 */
export const BUILDER_PLAN_SLUG = 'builder';

/** Slug for the Flex plan. Mirrors `FlexPlanSlug` in customerio/services. */
export const FLEX_PLAN_SLUG = 'flex_2026';

/** Slug for the Essentials plan. Mirrors `ESSENTIALS_PLAN_SLUG` in customerio/ui. */
export const ESSENTIALS_PLAN_SLUG = '202301_pricing_essentials';

export function isBuilderPlan(
  // Partial: a malformed or stale payload can arrive without the slug, and the
  // comparison below is what makes that read as "not Builder".
  account: Partial<Pick<Account, 'plan_slug'>> | Partial<Pick<RawAccount, 'plan_slug'>> | null | undefined,
) {
  return account?.plan_slug === BUILDER_PLAN_SLUG;
}

export function isFlexPlan(
  // Partial, as in `isBuilderPlan`. A payload with no slug is not Flex.
  account: Partial<Pick<Account, 'plan_slug'>> | Partial<Pick<RawAccount, 'plan_slug'>> | null | undefined,
) {
  return account?.plan_slug === FLEX_PLAN_SLUG;
}

/**
 * Whether the account gets Builder's transactional flow: every Builder account, and Flex and
 * Essentials accounts with `simplified-transactional` on. Mirrors `hasSimplifiedTransactional` in
 * customerio/ui's billing service.
 */
export function hasSimplifiedTransactional(
  account: (Pick<AugmentedAccount, 'feature_flags'> & Partial<Pick<AugmentedAccount, 'plan_slug'>>) | null | undefined,
) {
  if (isBuilderPlan(account)) return true;
  const isFlexOrEssentials = isFlexPlan(account) || account?.plan_slug === ESSENTIALS_PLAN_SLUG;
  return isFlexOrEssentials && accountHasFeatureFlag(account, AccountFeatureFlag.SimplifiedTransactional);
}
